import { useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router";
import { hasActiveSubscription } from "../shared/plans";
import { AuthSplash, SignInRedirect } from "./auth/AuthView";
import { useAuth } from "./auth/AuthContext";
import { BoardEntryView } from "./board/BoardEntryView";
import { BoardView } from "./board/BoardView";
import { AppShell } from "./components/AppShell";
import { flashsuitePages } from "./components/FlashsuiteMenu";
import { NotOnYet } from "./components/NotOnYet";
import { SendingProvider } from "./sending/SendingContext";
import { SettingsView } from "./settings/SettingsView";
import { RecordsPage } from "./setup/RecordsPage";
import { SetupProvider } from "./setup/SetupContext";
import { SetupView } from "./setup/SetupView";
import { LiveDoorView } from "./public/LiveDoorView";
import { PublicPageView } from "./public/PublicPageView";
import { inApp } from "./lib/inApp";
import { legacyRoomPath } from "./lib/legacyRoomPath";
import { isTenantHost } from "./lib/tenant";
import { currentWorkspaceSlug, workspaceHome } from "./lib/workspace";
import { setWorkspaceZone } from "./lib/zones";
import { TheirPage } from "./public/page/TheirPage";
import { WatchPageView } from "./public/WatchPageView";
import { AssetsView } from "./views/AssetsView";
import { AutomationsView } from "./views/AutomationsView";
import { AnnouncementsScreen } from "./talk/Announcements";
import { CircleScreen } from "./talk/Circle";
import { DirectScreen } from "./talk/Direct";
import { EveryoneScreen, RoomIndex } from "./talk/Everyone";
import { FilesScreen } from "./talk/Files";
import { QuestionsScreen } from "./talk/RoomQuestions";
import { TalkHome } from "./talk/Home";
import { AllRoomsScreen, PlacesScreen } from "./talk/PhoneRooms";
import { SearchScreen } from "./talk/Search";
import { TalkLayout } from "./talk/TalkLayout";
import { HomeView } from "./views/home/HomeView";
import { LiveStudioView } from "./views/LiveStudioView";
import { MoreView } from "./views/MoreView";
import { PeopleView } from "./views/people/PeopleView";
import { RoomsView } from "./views/rooms/RoomsView";
import { QuickLiveView } from "./views/rooms/QuickLiveView";
import { RoomView } from "./views/rooms/RoomView";
import { SessionReportView } from "./views/rooms/SessionReportView";

/**
 * The operator app (auth-guarded). On load the auth context checks the session
 * (`GET /auth/me`): while it resolves we show a splash; no session sends the
 * browser to sign in at Flashsuite; a live session renders the operator shell. Every top bar tab
 * is a route inside it: the stable contract the B-nodes build their views on.
 */
function OperatorApp() {
  const { status, user, flashsuite, refresh } = useAuth();
  // Before any page draws: every time the app shows reads the workspace's zone.
  setWorkspaceZone(user?.workspace_timezone);
  const slug = currentWorkspaceSlug();
  const tenant = typeof window !== "undefined" && isTenantHost(window.location.hostname);
  // Signed in at an address with no workspace on it: go to the session's
  // workspace, keeping the page. Signed in at another workspace's address
  // (a login from an old tab): ask again, so the seat there is the user.
  useEffect(() => {
    if (status !== "authed" || !user || tenant) return;
    if (!slug) {
      window.location.replace(workspaceHome(user.subdomain, true));
      return;
    }
    if (user.subdomain !== slug) void refresh();
  }, [status, user, slug, tenant, refresh]);
  // No Flashroom plan: Flashsuite's Start page, where the owner picks one and anyone else is told who
  // can. Platform admins (staff) work without one; the server also gates every money/reach action
  // (see billing/gate.ts). Inside the phone app nothing is sold, so it says so in one sentence instead.
  const start = inApp() ? undefined : flashsuitePages(flashsuite)?.start;
  const unplanned = !!user && !user.is_platform_admin && !hasActiveSubscription(user.plan_status);
  useEffect(() => {
    if (status === "authed" && unplanned && start) window.location.replace(start);
  }, [status, unplanned, start]);

  if (status === "loading") return <AuthSplash />;
  if (status === "anon") return <SignInRedirect />;
  if (user && !tenant && (!slug || user.subdomain !== slug)) return <AuthSplash />;
  if (unplanned) return user && inApp() ? <NotOnYet business={user.workspace_name} /> : <AuthSplash />;

  return (
    <SendingProvider>
      <SetupProvider>
        <Routes>
          {/* First-run setup is a full screen of its own, outside the shell. */}
          <Route path="setup/*" element={<SetupView />} />
          {/* A live's studio is the live room itself, full screen: no app menu around it. */}
          <Route path="rooms/:id/studio" element={<LiveStudioView />} />
          <Route element={<AppShell />}>
            <Route index element={<HomeView />} />
            <Route path="rooms" element={<RoomsView />} />
            {/* A new room, the waiting stage and the board are pages in the app, under the shell's own breadcrumb bar. */}
            <Route path="rooms/new" element={<BoardEntryView />} />
            <Route path="rooms/:id" element={<RoomView />} />
            {/* A quick live's own page: the call, its replay, who came and the follow-up. */}
            <Route path="rooms/:id/call" element={<QuickLiveView />} />
            {/* A room's tabs: live, sessions, page, followups. */}
            <Route path="rooms/:id/:tab" element={<RoomView />} />
            {/* A room's board (Walk it through), resumed: `:id` is the draft it already minted. */}
            <Route path="rooms/:id/walk" element={<BoardView />} />
            {/* How one live session went, at its own address, so it can be
                linked, bookmarked, refreshed and shared. A recording has no
                route of its own: its one session is the room's page. */}
            <Route path="rooms/:id/sessions/:sessionId" element={<SessionReportView />} />
            {/* Emails, notifications and bookmarks still carry the old addresses. */}
            <Route path="webinars/*" element={<LegacyRoomRedirect />} />
            <Route path="assets" element={<AssetsView />} />
            <Route path="assets/:folder" element={<AssetsView />} />
            <Route path="people" element={<PeopleView />} />
            <Route path="people/:id" element={<PeopleView />} />
            <Route path="automations" element={<AutomationsView />} />
            <Route path="automations/:id" element={<AutomationsView />} />
            {/* Conversations: a rail with each place beside it; on a phone the tab, and the places pushed over it. */}
            <Route path="conversations" element={<TalkLayout />}>
              <Route index element={<TalkHome />} />
              <Route path="rooms" element={<AllRoomsScreen />} />
              <Route path="rooms/:wid" element={<RoomIndex />} />
              <Route path="rooms/:wid/everyone" element={<EveryoneScreen />} />
              <Route path="rooms/:wid/places" element={<PlacesScreen />} />
              <Route path="rooms/:wid/announcements" element={<AnnouncementsScreen />} />
              <Route path="rooms/:wid/files" element={<FilesScreen />} />
              <Route path="rooms/:wid/questions" element={<QuestionsScreen />} />
              <Route path="circles/:cid" element={<CircleScreen />} />
              <Route path="direct/:contactId" element={<DirectScreen />} />
              <Route path="search" element={<SearchScreen />} />
            </Route>
            <Route path="settings" element={<SettingsView />} />
            <Route path="settings/:pane" element={<SettingsView />} />
            <Route path="settings/:pane/:sub" element={<SettingsView />} />
            {/* The phone's More tab: what the top bar holds on a wider screen. */}
            <Route path="more" element={<MoreView />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </SetupProvider>
    </SendingProvider>
  );
}

/** An old `/webinars...` address, sent on to the same screen under Rooms, keeping its query and hash. */
function LegacyRoomRedirect() {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={`${legacyRoomPath(pathname) ?? "/rooms"}${search}${hash}`} replace />;
}

/**
 * Top-level route split. The public funnel, the tenant-branded register page
 * (`/w/:slug`), the Watch page a registrant lands on (`/w/:slug/watch`) and the
 * permanent living-page URL (`/watch/:token`), is served OUTSIDE the operator auth
 * guard: a registrant has no operator session, so these routes never gate on
 * `GET /auth/me`. Everything else is the operator app.
 */
export function App() {
  const tenant = typeof window !== "undefined" && isTenantHost(window.location.hostname);
  return (
    <Routes>
      {/* Where a registrant lands: the talk, and the chat with the host. */}
      <Route path="/w/:slug/watch" element={<WatchPageView />} />
      <Route path="/w/:slug" element={<PublicPageView />} />
      {/* On a tenant's own address the path is the slug: that is what register_url
          hands out, and the API reads the workspace off the Host header. */}
      {tenant ? <Route path="/:slug" element={<PublicPageView />} /> : null}
      <Route path="/watch/:token" element={<TheirPage />} />
      {/* A quick live's link: joined with a first name and an email, then the Room. */}
      <Route path="/live/:code" element={<LiveDoorView />} />
      {/* The records a business sent whoever runs its website: no sign-in. */}
      <Route path="/records/:token" element={<RecordsPage />} />
      <Route path="/*" element={<OperatorApp />} />
    </Routes>
  );
}
